import { useState } from 'react';
import { Search as SearchIcon, ScanLine } from 'lucide-react';
import Button from '@/components/ui/button.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import ClienteCombobox from '@/components/ui/ClienteCombobox.jsx';
import SearchClientsModal from '@/components/Search/SearchClientsModal.jsx';
import QuickCreateClienteModal from '@/components/clientes/QuickCreateClienteModal.jsx';

/**
 * ClienteSelectWithModal - Selector de cliente con búsqueda avanzada
 * 
 * Features:
 * - Combobox con búsqueda en tiempo real (escribir para buscar)
 * - Botón de búsqueda avanzada que abre modal con filtros múltiples
 * - Modal de búsqueda incluye botón "Crear cliente" para alta rápida
 * - Validación automática para evitar duplicados (remitente ≠ destinatario)
 * - Auto-completado de datos del cliente al seleccionar
 */
export default function ClienteSelectWithModal({
  id,
  name,
  label,
  value,
  selectedCliente = null,
  onChange,
  onClienteSelect,
  error,
  placeholder = 'Buscar cliente...',
  disabled = false,
  excludeId = null,
  allowEventualDni = false,
}) {
  const { can } = useAuth();
  const [createMode, setCreateMode] = useState('general');
  const canCreateEventual = can('CLIENTES_CREAR') && can('CLIENTES_VER');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleClienteSelectFromCombobox = (cliente) => {
    // Callback para autocompletar otros campos si se proporciona
    if (onClienteSelect && cliente) {
      onClienteSelect(cliente);
    }
  };
  
  const handleSelectFromModal = (cliente) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: cliente.id,
        },
      });
    }
    
    // Callback para autocompletar otros campos
    if (onClienteSelect) {
      onClienteSelect(cliente);
    }
    
    setIsSearchModalOpen(false);
  };

  const handleClienteCreated = (nuevoCliente) => {
    if (createMode !== 'general' && String(nuevoCliente?.id) === String(excludeId)) return;
    // Cerrar modal de creación
    setIsCreateModalOpen(false);
    
    // Seleccionar el nuevo cliente automáticamente
    if (nuevoCliente && nuevoCliente.id && onChange) {
      onChange({
        target: {
          name,
          value: nuevoCliente.id,
        },
      });
      
      // Callback para autocompletar
      if (onClienteSelect) {
        onClienteSelect(nuevoCliente);
      }
    }
    
    // Cerrar también el modal de búsqueda
    setIsSearchModalOpen(false);
  };

  const handleOpenCreateFromSearch = () => {
    // No cerrar el modal de búsqueda, abrir creación por encima
    setCreateMode('general');
    setIsCreateModalOpen(true);
  };

  return (
    <>
      <div className="flex min-w-0 flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <ClienteCombobox
            id={id}
            name={name}
            label={label}
            value={value}
            selectedCliente={selectedCliente}
            onChange={onChange}
            onClienteSelect={handleClienteSelectFromCombobox}
            error={error}
            placeholder={placeholder}
            disabled={disabled}
            excludeId={excludeId}
          />
        </div>
        
        <Button
          type="button"
          variant="outline"
          size="default"
          onClick={() => setIsSearchModalOpen(true)}
          disabled={disabled}
          className="h-9 shrink-0"
          title="Búsqueda avanzada de clientes"
        >
          <SearchIcon size={18} />
          <span className="hidden sm:inline">Buscar</span>
        </Button>
        {allowEventualDni && <>
          <Button type="button" variant="outline" disabled={disabled || !canCreateEventual} className="h-9 shrink-0 px-2 text-xs" onClick={() => { setCreateMode('manual'); setIsCreateModalOpen(true); }}>Nuevo eventual</Button>
          <Button type="button" variant="outline" disabled={disabled || !can('CLIENTES_VER')} className="h-9 shrink-0 px-2 text-xs" onClick={() => { setCreateMode('scan'); setIsCreateModalOpen(true); }}><ScanLine size={16} />Escanear DNI</Button>
        </>}
      </div>

      {/* Modal de búsqueda con integración de creación */}
      <SearchClientsModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelect={handleSelectFromModal}
        onOpenCreate={handleOpenCreateFromSearch}
        excludeId={excludeId}
      />

      {/* Modal de creación rápida */}
      <QuickCreateClienteModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onComplete={handleClienteCreated}
        eventualMode={allowEventualDni && createMode !== 'general'}
        initialCapture={createMode === 'scan'}
        excludeId={excludeId}
      />
    </>
  );
}
